import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ChevronLeft, Info, Loader2, Timer } from "lucide-react";
import { API, JURISDICTIONS } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const CLAIM_TYPES = [
  { value: "personal_injury", label: "Personal Injury" },
  { value: "medical_malpractice", label: "Medical Malpractice" },
  { value: "contract_written", label: "Contract (Written)" },
  { value: "contract_oral", label: "Contract (Oral)" },
  { value: "fraud", label: "Fraud" },
  { value: "property_damage", label: "Property Damage" },
  { value: "wrongful_death", label: "Wrongful Death" },
  { value: "legal_malpractice", label: "Legal Malpractice" },
  { value: "product_liability", label: "Product Liability" },
  { value: "defamation", label: "Defamation" }
];

const StatuteOfLimitationsPage = () => {
  const [form, setForm] = useState({
    jurisdiction: "california",
    claim_type: "personal_injury",
    incident_date: new Date().toISOString().split('T')[0]
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const calculate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/statute-of-limitations/calculate`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const getUrgencyColor = (urgency) => {
    const colors = {
      "EXPIRED": "bg-black text-white",
      "CRITICAL": "bg-red-600 text-white",
      "URGENT": "bg-orange-500 text-white",
      "ATTENTION": "bg-amber-500 text-white",
      "NORMAL": "bg-emerald-500 text-white"
    };
    return colors[urgency] || "bg-slate-500 text-white";
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="sol-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Statute of Limitations</h1>
          <p className="text-muted-foreground">Calculate filing deadlines by claim type and jurisdiction</p>
        </div>
        
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            Statutes of limitations have many exceptions, tolling provisions, and discovery rules. 
            Always verify deadlines with local counsel and current statutes.
          </AlertDescription>
        </Alert>
        
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Calculate Deadline</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select value={form.jurisdiction} onValueChange={v => setForm(p => ({ ...p, jurisdiction: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label>Claim Type</Label>
                <Select value={form.claim_type} onValueChange={v => setForm(p => ({ ...p, claim_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLAIM_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-1.5">
                <Label>Incident/Accrual Date</Label>
                <Input 
                  type="date" 
                  value={form.incident_date}
                  onChange={e => setForm(p => ({ ...p, incident_date: e.target.value }))}
                />
              </div>
              
              <Button className="w-full" onClick={calculate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Timer className="h-4 w-4 mr-2" />}
                Calculate Deadline
              </Button>
            </CardContent>
          </Card>
          
          {result && result.status === "success" && (
            <Card className="animate-fade-in">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Filing Deadline</CardTitle>
                  <Badge className={getUrgencyColor(result.urgency)}>{result.urgency}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-primary/5 rounded-xl text-center">
                  <p className="text-sm text-muted-foreground mb-1">Deadline Date</p>
                  <p className="text-4xl font-bold text-primary">{result.deadline}</p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-sm text-muted-foreground mb-1">Days Remaining</p>
                  <p className="text-3xl font-bold">{result.days_remaining}</p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Limitation Period</span>
                    <span className="font-medium">
                      {result.limitation_period.years ? `${result.limitation_period.years} years` : `${result.limitation_period.days} days`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Statute Reference</span>
                    <span className="font-medium">{result.statute_reference}</span>
                  </div>
                </div>
                
                <Alert className="bg-slate-100 border-slate-200">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    {result.warning}
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default StatuteOfLimitationsPage;
